// FreeHashtagResearch.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Modal from '../components/modal';
import SignupForm from '../components/auth/SignupForm';
import LoginForm from '../components/auth/LoginForm';
import HashtagPromptWizard from '../components/HashtagPromptWizard';
import './FreeHashtagResearch.css';

const MAX_FREE_SEARCHES = 500;
const LOCAL_KEY = 'chiclogic_free_search_count';
const ENGINE_BASE = (process.env.REACT_APP_CHIC_ENGINE_ENDPOINT || '').replace(/\/+$/, '');
const HASHTAG_ENQUEUE_ENDPOINT = `${ENGINE_BASE}/api/v1/intelligence/instagram-suggest-hashtags`;
const TASK_STATUS_ENDPOINT = `${ENGINE_BASE}/api/v1/intelligence/tasks`;
const API_KEY = process.env.REACT_APP_CHICLOGIC_API_KEY;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatAudience = (context) => {
  if (!context) return undefined;
  const region = context.audience_region?.trim();
  const city = context.audience_city?.trim();
  if (region === 'local') {
    return city ? `local:${city}` : 'local';
  }
  if (city) return city;
  return region || undefined;
};

const buildHashtagPayload = (caption, context) => {
  const payload = {
    text: caption.trim(),
    max_hashtags: 30,
  };
  if (!payload.text) return null;
  if (context?.goal) payload.goal = context.goal;
  if (context?.niche) payload.niche = context.niche;
  const audience = formatAudience(context);
  if (audience) payload.audience = audience;
  return payload;
};

const sanitizePseudoJson = (value) => {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;

  const keyNormalized = trimmed.replace(/([{,]\s*)'([^']+?)'(?=\s*:)/g, (_, prefix, key) => `${prefix}"${key}"`);
  const valueNormalized = keyNormalized.replace(/:\s*'([^']*)'/g, (_, val) => `: "${val.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);

  try {
    return JSON.parse(valueNormalized);
  } catch {
    return null;
  }
};

const normalizeHashtagResult = (result) => {
  if (!result) return { hashtags: [], reason: '' };

  const container = Array.isArray(result) ? { hashtags: result } : result;
  const rawList = Array.isArray(container?.hashtags)
    ? container.hashtags
    : Array.isArray(container?.result?.hashtags)
      ? container.result.hashtags
      : [];

  const normalized = [];
  const seen = new Set();

  rawList.forEach((entry) => {
    if (!entry) return;
    let tagText = '';
    let views = null;
    let rank;

    if (typeof entry === 'string') {
      const parsed = sanitizePseudoJson(entry) || null;
      if (parsed) {
        tagText = parsed.hashtag || parsed.tag || parsed.name || '';
        views = parsed.views ?? parsed.estimated_views ?? null;
        if (parsed.rank != null) rank = parsed.rank;
      } else {
        tagText = entry;
      }
    } else if (typeof entry === 'object') {
      tagText = entry.hashtag || entry.tag || entry.name || '';
      views = entry.estimated_views ?? entry.views ?? null;
      if (entry.rank != null) rank = entry.rank;
    }

    tagText = String(tagText || '').trim();
    if (!tagText) return;
    const stripped = tagText.replace(/^#+/, '');
    if (!stripped) return;
    const display = `#${stripped}`;
    const key = display.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);

    normalized.push({
      hashtag: display,
      rank: rank != null ? rank : normalized.length + 1,
      estimated_views: views,
    });
  });

  normalized.sort((a, b) => (a.rank ?? 9999) - (b.rank ?? 9999));

  return {
    hashtags: normalized,
    reason: container.reason || container?.result?.reason || '',
  };
};

const FreeHashtagResearch = () => {
  const navigate = useNavigate();
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState([]);           // [{hashtag, rank, estimated_views}]
  const [reason, setReason] = useState('');                // NEW
  const [searchCount, setSearchCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  // Wizard state
  const [wizardDone, setWizardDone] = useState(false);
  const [wizardContext, setWizardContext] = useState(null);
  const inputCardRef = useRef(null);
  const activeRequestIdRef = useRef(0);

  // Selection + copy
  const [selectedTags, setSelectedTags] = useState(new Set());  // stores strings like "#TechTools"
  const [copyMsg, setCopyMsg] = useState('');

  const toggleSelect = (tagStr) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      next.has(tagStr) ? next.delete(tagStr) : next.add(tagStr);
      return next;
    });
  };
  const selectAll = () => setSelectedTags(new Set(hashtags.map(h => h.hashtag)));
  const clearAll  = () => setSelectedTags(new Set());

  const copyToClipboard = async (tagStrings) => {
    const list = Array.from(tagStrings);
    const text = list.join(' ');  // space-delimited
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopyMsg(`Copied ${list.length} hashtag${list.length === 1 ? '' : 's'} to clipboard`);
    setTimeout(() => setCopyMsg(''), 1800);
  };

  useEffect(() => {
    const count = parseInt(localStorage.getItem(LOCAL_KEY) || '0', 10);
    setSearchCount(count);
    setIsBlocked(count >= MAX_FREE_SEARCHES);
  }, []);

  useEffect(() => {
    if (wizardDone && inputCardRef.current) {
      inputCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [wizardDone]);

  const formatViews = (n) => {
    if (n == null) return '';
    if (n >= 1_000_000) return `${(n/1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`;
    if (n >= 1_000)     return `${(n/1_000).toFixed(n % 1_000 ? 1 : 0)}K`;
    return n.toLocaleString();
  };

  const handleSearch = async () => {
    if (isBlocked) return;
    const payload = buildHashtagPayload(caption, wizardContext);
    if (!payload) return;

    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    setLoading(true);

    const pollForResult = async (taskId) => {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
        if (activeRequestIdRef.current !== requestId) return null;

        const pollResp = await fetch(`${TASK_STATUS_ENDPOINT}/${taskId}?wait_seconds=2`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Api-Key': API_KEY,
          },
        });

        let pollData;
        if (pollResp.ok) {
          pollData = await pollResp.json();
        } else {
          let message = 'Failed to retrieve hashtag job status.';
          try {
            const errPayload = await pollResp.json();
            message = errPayload?.detail || message;
          } catch {
            // ignore JSON parse failures
          }
          throw new Error(message);
        }

        const status = (pollData.status || '').toUpperCase();
        if (status === 'SUCCESS') {
          return pollData.result ?? pollData;
        }
        if (status === 'FAILURE' || status === 'REVOKED') {
          throw new Error(pollData.error || 'Hashtag job failed. Please try again.');
        }

        await sleep(POLL_INTERVAL_MS);
      }

      throw new Error('Timed out waiting for hashtag results. Please try again.');
    };

    try {
      const enqueueResp = await fetch(HASHTAG_ENQUEUE_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Api-Key': API_KEY,
        },
        body: JSON.stringify(payload),
      });

      let enqueueData;
      if (enqueueResp.ok) {
        enqueueData = await enqueueResp.json();
      } else {
        let message = 'Failed to queue hashtag job.';
        try {
          const errPayload = await enqueueResp.json();
          message = errPayload?.detail || message;
        } catch {
          // ignore JSON parse failures
        }
        throw new Error(message);
      }

      const taskId = enqueueData?.task_id;
      if (!taskId) {
        throw new Error('Missing task id from hashtag API response.');
      }

      const taskResult = await pollForResult(taskId);
      if (!taskResult || activeRequestIdRef.current !== requestId) {
        return;
      }

      const { hashtags: normalizedTags, reason: newReason } = normalizeHashtagResult(taskResult);
      setHashtags(normalizedTags);
      setReason(newReason || '');
      setSelectedTags(new Set());

      const newCount = searchCount + 1;
      setSearchCount(newCount);
      localStorage.setItem(LOCAL_KEY, String(newCount));
      if (newCount >= MAX_FREE_SEARCHES) setIsBlocked(true);
    } catch (err) {
      console.error(err);
      if (activeRequestIdRef.current === requestId) {
        alert(err?.message || 'Something went wrong while fetching hashtags.');
      }
    } finally {
      if (activeRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Free Instagram Hashtag Research Tool | ChicLogic.io</title>
        <meta name="description" content="Use ChicLogic’s free AI hashtag research tool to get high-performing Instagram hashtags instantly." />
      </Helmet>

      <div className="hashtag-page-wrapper">
        <div className="centered-header">
          <h1>Free Instagram Hashtag Research Tool</h1>
          <p>Answer a few quick questions—then paste your caption for perfectly tailored hashtags.</p>
        </div>

        {!wizardDone && (
          <HashtagPromptWizard
            onComplete={(ctx) => { setWizardContext(ctx); setWizardDone(true); }}
          />
        )}

        {wizardDone && (
          <div ref={inputCardRef} className="input-card chic-fade-in">
            <div className="wizard-summary-row">
              <span className="wizard-summary-chip" title="Content type">
                {({ photo: '📷', carousel: '🗂️', reel: '🎞️', story: '🫧', live: '🎤' }[wizardContext?.content_type]) || '🧩'}
                <b style={{ marginLeft: 6 }}>{(wizardContext?.content_type || '').toString().toUpperCase()}</b>
              </span>
              <span className="wizard-summary-chip" title="Goal">
                {({ reach: '📈', saves: '💾', affiliate: '🛒', engagement: '💬', authority: '🧩' }[wizardContext?.goal]) || '🎯'}
                <b style={{ marginLeft: 6 }}>{(wizardContext?.goal || '').toString().toUpperCase()}</b>
              </span>
              {wizardContext?.niche && (
                <span className="wizard-summary-chip" title="Niche">
                  🧵<b style={{ marginLeft: 6 }}>{wizardContext.niche.toString().toUpperCase()}</b>
                </span>
              )}
              <button className="ghost-button" onClick={() => setWizardDone(false)} title="Edit your answers">Edit answers</button>
            </div>

            <textarea
              className="caption-textarea"
              placeholder="Paste your Instagram post or reel caption here..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />

            <button className="caption-search-btn" onClick={handleSearch} disabled={loading || caption.trim() === ''}>
              {loading ? 'Generating…' : 'Get Hashtag Suggestions'}
            </button>

            {!isBlocked && (
              <p className="search-count">
                {MAX_FREE_SEARCHES - searchCount} free search{MAX_FREE_SEARCHES - searchCount !== 1 ? 'es' : ''} remaining
              </p>
            )}
          </div>
        )}

        {/* Results */}
        {hashtags.length > 0 && (
          <div className="results-box chic-fade-in">
            {/* H2 now uses the backend "reason" */}
            <div className="results-header">
              <div className="results-reason">{reason || 'Suggested hashtags tailored to your context'}</div>
            </div>

            {/* Action bar */}
            <div className="results-actions">
              <div className="results-actions-left">
                <button type="button" className="results-chip-btn" onClick={selectAll}>Select All</button>
                <button type="button" className="results-chip-btn" onClick={clearAll}>Clear</button>
                <span className="results-count">{selectedTags.size} selected</span>
              </div>

              <div className="results-actions-right">
                <button
                  type="button"
                  className="caption-search-btn"
                  onClick={() => copyToClipboard(hashtags.map(h => h.hashtag))}
                  title="Copy all hashtags"
                >
                  Copy All
                </button>
                <button
                  type="button"
                  className="caption-search-btn solid"
                  onClick={() => copyToClipboard(hashtags.filter(h => selectedTags.has(h.hashtag)).map(h => h.hashtag))}
                  disabled={selectedTags.size === 0}
                  title="Copy only selected hashtags"
                >
                  Copy Selected
                </button>
              </div>
            </div>

            {/* Hashtag grid (ordered by rank) */}
            <ul className="hashtags-list" role="listbox" aria-label="Hashtags">
              {hashtags.map((h) => {
                const tagStr = h.hashtag;
                const isActive = selectedTags.has(tagStr);
                return (
                  <li key={tagStr}>
                    <button
                      type="button"
                      className={`hashtag-pill ${isActive ? 'is-selected' : ''}`}
                      onClick={() => toggleSelect(tagStr)}
                      role="option"
                      aria-selected={isActive}
                      title={isActive ? 'Click to unselect' : 'Click to select'}
                    >
                      <span className="pill-check" aria-hidden="true">{isActive ? '✓' : ''}</span>
                      <span className="pill-text">{tagStr}</span>
                      <span className="pill-views" aria-label="Estimated views">
                        {formatViews(h.estimated_views)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {copyMsg && <div className="copy-toast">{copyMsg}</div>}
          </div>
        )}

        {isBlocked && (
          <div className="blocked-box">
            <p className="blocked-title">You’ve used all free searches.</p>
            <p className="blocked-subtext">Create a free account to unlock unlimited hashtag suggestions and save bundles.</p>
            <button type="button" className="signup-button" onClick={() => setShowSignup(true)}>Create Free Account</button>
            <p className="blocked-subtext" style={{ marginTop: '1rem' }}>
              Already have an account?
              {' '}
              <button
                type="button"
                className="ghost-button"
                onClick={() => setShowLogin(true)}
                style={{ padding: 0, fontSize: 'inherit' }}
              >
                Sign in
              </button>
            </p>
          </div>
        )}
      </div>

      <Modal open={showSignup} onClose={() => setShowSignup(false)} title="Create your ChicLogic.io account">
        <SignupForm
          onClose={() => setShowSignup(false)}
          onSuccess={() => {
            setShowSignup(false);
            navigate('/signup/thank-you');
          }}
          variant="modal"
        />
      </Modal>
      <Modal open={showLogin} onClose={() => setShowLogin(false)} title="Login and thrive">
        <LoginForm onClose={() => setShowLogin(false)} />
      </Modal>
    </>
  );
};

export default FreeHashtagResearch;
